import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';

const AddInvoiceItemType = ({ type, onClose }) => {
  const isEditMode = type && type.id;
  
  const [form, setForm] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      setForm({
        name: type.name || '',
        description: type.description || ''
      });
    } else {
      setForm({
        name: '',
        description: ''
      });
    }
  }, [type, isEditMode]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Invoice item type name is required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      if (isEditMode) {
        // Update existing type using POST
        await apiClient.post(`/invoicing/invoice-item-types/${type.id}`, form);
        toastSuccess('Invoice item type updated successfully');
      } else {
        // Create new type
        const response = await apiClient.post('/invoicing/invoice-item-types', form);
        
        // Check if it was a reactivation
        if (response.data?.was_reactivated) {
          toastSuccess('Invoice item type was reactivated successfully');
        } else {
          toastSuccess('Invoice item type created successfully');
        }
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving invoice item type:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save invoice item type';
      setErrors({ submit: errorMessage });
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div>
      <div style={{ background: 'transparent' }}>
        <h2 className="rt-title">{isEditMode ? 'Edit Invoice Item Type' : 'Add Invoice Item Type'}</h2>
      </div>
      
      <div className="rt-main-section">
        <form onSubmit={handleSubmit} className="complaint-form">
          <div className="complaint-form-sections">
            <div className="complaint-section">
              <h3 className="complaint-section-title">Invoice Item Type Details</h3>
              
              <div className="complaint-form-group">
                <label>
                  Type Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter invoice item type name (e.g., Bed Rent, EB Charges)"
                  required
                  disabled={loading}
                />
                {errors.name && (
                  <span className="form-error">{errors.name}</span>
                )}
              </div>

              <div className="complaint-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Enter description (optional)"
                  rows="4"
                  disabled={loading}
                />
                {errors.description && (
                  <span className="form-error">{errors.description}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Loading Overlay */}
          {loading && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999
            }}>
              <RTSpinner size="medium" showMessage={true} message="Saving invoice item type..." />
            </div>
          )}
          
          <div className="complaint-actions">
            <RTButton 
              type="button" 
              variant="secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </RTButton>
            <RTButton 
              type="submit" 
              variant="primary" 
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Type' : 'Create Type')}
            </RTButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInvoiceItemType;
